import { useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { PROCESS_BACKGROUND_REMOVAL } from '../lib/apollo/mutations/processBackgroundRemoval'
import { canSelectTalentCardType, getParticipantImageUrl } from '../lib/helpers/talentCardHelpers'
import { useBackgroundRemovalDetection } from './useBackgroundRemovalDetection'

interface UseParticipantImageWithProcessingOptions {
  autoTrigger?: boolean
  onProcessingStart?: (participantId: string) => void
  onProcessingComplete?: (participantId: string, result: any) => void
  onProcessingError?: (participantId: string, error: any) => void
}

// Track which participants have already been processed in this session
const processedParticipants = new Set<string>()

export const useParticipantImageWithProcessing = (
  participant: any,
  camp: any,
  options: UseParticipantImageWithProcessingOptions = {}
) => {
  const {
    autoTrigger = true,
    onProcessingStart,
    onProcessingComplete,
    onProcessingError
  } = options

  // Get the current image URL using the existing logic
  const imageUrl = getParticipantImageUrl(participant, camp)

  // Check if we need background removal
  const needsBackgroundRemoval =
    participant &&
    camp?.end &&
    canSelectTalentCardType(camp.end) && // Camp is eligible for new talent cards
    (
      // Case 1: Has imageWithBackground but no processed image
      (participant.imageWithBackground && !participant.image && !participant.imageWithBackground.includes('default_talentcard.png')) ||
      // Case 2: Legacy case - only has image (which contains background) and no imageWithBackground
      (participant.image && !participant.imageWithBackground && !participant.image.includes('default_talentcard.png') && !participant.image.includes('_no_bg'))
    )

  const {
    isProcessing,
    markAsProcessing,
    markAsCompleted
  } = useBackgroundRemovalDetection(participant)

  const [processBackgroundRemoval, { loading: mutationLoading }] = useMutation(
    PROCESS_BACKGROUND_REMOVAL,
    {
      onCompleted: (data) => {
        if (participant?.id) {
          markAsCompleted(participant.id)
          onProcessingComplete?.(participant.id, data)
        }
      },
      onError: (error) => {
        if (participant?.id) {
          markAsCompleted(participant.id)
          onProcessingError?.(participant.id, error)
        }
      },
      refetchQueries: ['GetCertificate', 'GetParticipant', 'participants', 'camp'],
      awaitRefetchQueries: true
    }
  )

  useEffect(() => {
    // Check if already processed
    if (participant?.id && processedParticipants.has(participant.id)) {
      return
    }

    if (!autoTrigger || !needsBackgroundRemoval || isProcessing || mutationLoading) {
      if (!needsBackgroundRemoval && participant?.id) {
        console.log('🔍 useParticipantImageWithProcessing - No processing needed for participant:', {
          participantId: participant.id,
          hasImage: !!participant.image,
          hasImageWithBackground: !!participant.imageWithBackground,
          imageUrl: participant.image,
          imageWithBackgroundUrl: participant.imageWithBackground
        })
      }
      return
    }

    const triggerProcessing = async () => {
      try {
        // Mark as processed to prevent duplicate processing
        processedParticipants.add(participant.id)

        console.log('🚀 useParticipantImageWithProcessing - Starting background removal for participant:', {
          participantId: participant.id,
          reason: participant.imageWithBackground && !participant.image ? 'Has imageWithBackground but no processed image' : 'Legacy case - only has image field'
        })
        markAsProcessing(participant.id)
        onProcessingStart?.(participant.id)

        await processBackgroundRemoval({
          variables: { participantId: participant.id }
        })
      } catch (error) {
        console.error('Background removal failed:', error)
        // Remove from processed set to allow retry
        processedParticipants.delete(participant.id)
      }
    }

    // Delay to avoid rapid triggers
    const timeoutId = setTimeout(() => {
      void triggerProcessing()
    }, 2000)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [
    autoTrigger,
    needsBackgroundRemoval,
    isProcessing,
    mutationLoading,
    participant?.id,
    processBackgroundRemoval,
    markAsProcessing,
    onProcessingStart
  ])

  return {
    imageUrl,
    needsBackgroundRemoval,
    isProcessing: isProcessing || mutationLoading,
    triggerBackgroundRemoval: async () => {
      if (!participant?.id || !needsBackgroundRemoval) {
        return
      }

      markAsProcessing(participant.id)
      onProcessingStart?.(participant.id)

      return await processBackgroundRemoval({
        variables: { participantId: participant.id }
      })
    }
  }
}
